import { Alert } from '@winnin/winnin_ui.components.alert'
import { Button } from '@winnin/winnin_ui.components.button'
import { Title } from '@winnin/winnin_ui.components.title'

import { LoadingCircle } from 'assets/icons/loading-circle'
import { BaseSkeleton } from 'components/BaseSkeleton'
import { useTranslate } from 'lib/tolgee'
import { NicheDescription } from 'modules/niche/niche-description'

import { translations } from './niche-tab.i18n'
import { ViewState, type INicheTabView } from './niche-tab.types'
import { NICHE_LIMIT } from '../common/niche.consts'
import { ContextList } from '../context-list'
import { CreateNicheDashboardModal } from '../create-niche-dashboard-modal'
import { NicheEmptyState } from '../niche-empty-state'
import { NicheError } from '../niche-error'
import { NicheTable } from '../niche-table'
import { PendingNicheView } from '../pending-niche'

import './niche-tab.styles.scss'

export const NicheTabView = ({
  niches,
  viewState,
  tableTitle,
  loadingRowsCount,
  onRetry,
  isDefaultContext,
  handleDashboardModal,
  isCreateDashboardModalOpen,
  nicheForDashModal,
  territoryName,
  generatedNicheDescription,
  shouldDisplayContextDescription,
  isHappeningNow,
  handleOpenDashboardModal,
  handleTrackClick,
  entityId,
  ...contextManager
}: INicheTabView) => {
  const t = useTranslate()

  return (
    <div className="NicheTab">
      <ContextList {...contextManager} />
      <div className="NicheTab-content">
        {viewState === ViewState.ERROR && (
          <div className="NicheTab-errorState">
            <NicheError onRetry={onRetry} />
          </div>
        )}
        {viewState === ViewState.GENERATING ? (
          <PendingNicheView isSevenDays={isHappeningNow} />
        ) : (
          <>
            {isHappeningNow && (
              <div className="HappeningNowAlert">
                <Alert message={t(translations.happeningNowAlert({ nicheLimit: NICHE_LIMIT }))} />
              </div>
            )}
            {shouldDisplayContextDescription && (
              <div className="NicheDescriptionContent">
                <NicheDescription descriptionText={generatedNicheDescription} isLoading={!generatedNicheDescription} />
              </div>
            )}
            <div className="NicheTab-tableTitleRow">
              <div className="leftSection">
                <Title tag="h3">{tableTitle}</Title>
                {viewState === ViewState.FETCHING && (
                  <div className="NicheTab-loadingTitle">
                    <div className="NicheTab-loadingCircle">
                      <LoadingCircle />
                    </div>
                    {t(translations.generatingText)}
                  </div>
                )}
              </div>
              {[ViewState.COMPLETE, ViewState.FETCHING].includes(viewState) && (
                <Button
                  disabled={viewState === ViewState.FETCHING}
                  appearance="outline"
                  onClick={handleOpenDashboardModal}
                >
                  {t(translations.reportButton)}
                </Button>
              )}
            </div>
            {viewState !== ViewState.EMPTY && viewState !== ViewState.ERROR && (
              <NicheTable niches={niches} handleTrackClick={handleTrackClick} />
            )}
            {viewState === ViewState.FETCHING && (
              <div className="NicheTab-skeletonWrapper">
                {Array.from(Array(loadingRowsCount).keys()).map(item => {
                  return (
                    <div className="NicheTab-skeletonRow" key={item}>
                      <div />
                      <div className="NicheTab-skeletonNameCell">
                        <BaseSkeleton height="10px" width="150px" />
                      </div>
                      {Array.from(Array(4).keys()).map(cellIndex => (
                        <div className="NicheTab-valueCell" key={cellIndex}>
                          <BaseSkeleton height={10} borderRadius={16} width={80} />
                        </div>
                      ))}
                    </div>
                  )
                })}
              </div>
            )}
            {viewState === ViewState.EMPTY && (
              <div className="NicheTab-emptyState">
                <NicheEmptyState contextName={contextManager.activeContext.name} isDefaultContext={isDefaultContext} />
              </div>
            )}
          </>
        )}
      </div>
      {isCreateDashboardModalOpen && (
        <CreateNicheDashboardModal
          nicheList={nicheForDashModal}
          handleCloseModal={handleDashboardModal}
          isOpen={isCreateDashboardModalOpen}
          territoryName={territoryName}
          contextName={contextManager.activeContext.name}
          entityId={entityId}
        />
      )}
    </div>
  )
}
